import * as React from "react";
import { useEffect, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Box } from "@chakra-ui/react";
import {
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  useMap,
} from "react-leaflet";
import L, { LatLngExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";
import { decodePolyline } from "../../utils/decodePolyline";
import { getCarImage } from "../../utils/carImage";

interface TrackingMapProps {
  driverLat?: number;
  driverLng?: number;
  /** Polyline (formato Google) del recorrido origen → destino (en viaje). */
  polyline?: string;
  /** Polyline (formato Google) desde el conductor hasta el origen (pre-viaje). */
  polylineStarted?: string;
  /** Color del vehículo, para elegir la imagen del auto del conductor. */
  vehicleColor?: string;
  /** true cuando el recorrido ya arrancó (pasajero a bordo). */
  rideStarted?: boolean;
}

const ROUTE_COLOR = "#EAFE03";

// Sin API key, CARTO devuelve los tiles con la marca "API KEY REQUIRED".
const CARTO_API_KEY = process.env.REACT_APP_CARTO_API_KEY;
const TILE_URL = `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png${
  CARTO_API_KEY ? `?key=${CARTO_API_KEY}` : ""
}`;

// Centro por defecto: Córdoba, Argentina (cuando aún no hay conductor asignado).
const DEFAULT_CENTER: LatLngExpression = [-31.4201, -64.1888];

// Origen: círculo con otro círculo adentro, con glow.
const ORIGIN_ICON = L.divIcon({
  className: "",
  html: `<div style="
    width:28px;height:28px;border-radius:50%;
    border:3px solid ${ROUTE_COLOR};background:rgba(20,20,20,0.55);
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 0 12px 2px rgba(234,254,3,0.55);
  "><div style="width:11px;height:11px;border-radius:50%;background:${ROUTE_COLOR}"></div></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// Destino: pin (lucide MapPin) con glow, anclado en la punta.
const DESTINATION_ICON = L.divIcon({
  className: "",
  html: `<div style="line-height:0;filter:drop-shadow(0 0 6px rgba(234,254,3,0.7))">${renderToStaticMarkup(
    <MapPin size={42} color="#141414" fill={ROUTE_COLOR} strokeWidth={1.75} />,
  )}</div>`,
  iconSize: [42, 42],
  iconAnchor: [21, 41],
});

// Conductor en vivo: imagen del auto dentro de un círculo, con glow.
const carIcon = (src: string) =>
  L.divIcon({
    className: "",
    html: `<div style="
      width:40px;height:40px;border-radius:50%;
      background:#FFFFFF;border:2px solid ${ROUTE_COLOR};
      box-shadow:0 0 8px 2px rgba(234,254,3,0.5);
      display:flex;align-items:center;justify-content:center;overflow:hidden;
    "><img src="${src}" alt="" style="width:32px;height:22px;object-fit:contain" /></div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });

// Fallback del conductor cuando no hay imagen de auto: punto amarillo sólido.
const DRIVER_FALLBACK_ICON = L.divIcon({
  className: "",
  html: `<div style="
    width:20px;height:20px;border-radius:50%;
    background:${ROUTE_COLOR};border:2px solid #141414;
    box-shadow:0 0 10px 2px rgba(234,254,3,0.6);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

interface DerivedMarkers {
  origin?: LatLngExpression;
  destination?: LatLngExpression;
}

/**
 * - Pre-viaje (path = polylineStarted, conductor → origen): origen al final, sin destino.
 * - En viaje (path = polyline, origen → destino): origen al inicio, destino al final.
 */
const deriveMarkers = (
  path: LatLngExpression[],
  rideStarted: boolean
): DerivedMarkers => {
  if (path.length < 2) return {};
  const first = path[0];
  const last = path[path.length - 1];
  return rideStarted
    ? { origin: first, destination: last }
    : { origin: last };
};

/** Ajusta el encuadre del mapa a todos los puntos visibles. */
const FitBounds: React.FC<{ points: LatLngExpression[] }> = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [48, 48] });
  }, [map, points]);

  return null;
};

export const TrackingMap: React.FC<TrackingMapProps> = ({
  driverLat,
  driverLng,
  polyline,
  polylineStarted,
  vehicleColor,
  rideStarted = false,
}) => {
  const driverPosition = useMemo<LatLngExpression | undefined>(
    () =>
      typeof driverLat === "number" && typeof driverLng === "number"
        ? [driverLat, driverLng]
        : undefined,
    [driverLat, driverLng],
  );

  const driverIcon = useMemo(() => {
    const carImage = getCarImage(vehicleColor);
    return carImage ? carIcon(carImage) : DRIVER_FALLBACK_ICON;
  }, [vehicleColor]);

  // En viaje se usa la ruta origen → destino; antes, la ruta conductor → origen.
  const activePolyline = rideStarted ? polyline : polylineStarted;
  const path = useMemo<LatLngExpression[]>(
    () => (activePolyline ? decodePolyline(activePolyline) : []),
    [activePolyline],
  );

  const { origin, destination } = useMemo(
    () => deriveMarkers(path, rideStarted),
    [path, rideStarted]
  );

  const fitPoints = useMemo<LatLngExpression[]>(() => {
    const pts = [...path];
    if (driverPosition) pts.push(driverPosition);
    return pts;
  }, [path, driverPosition]);

  return (
    <Box h="full" w="full" bg="primary.900">
      <MapContainer
        center={driverPosition ?? DEFAULT_CENTER}
        zoom={driverPosition ? 15 : 12}
        zoomControl
        attributionControl
        style={{ height: "100%", width: "100%", background: "#1d1d1d" }}
      >
        <TileLayer
          url={TILE_URL}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        {path.length > 0 && (
          <Polyline
            positions={path}
            pathOptions={{ color: ROUTE_COLOR, weight: 5, opacity: 0.95 }}
          />
        )}
        {origin && <Marker position={origin} icon={ORIGIN_ICON} />}
        {destination && (
          <Marker position={destination} icon={DESTINATION_ICON} />
        )}
        {driverPosition && (
          <Marker position={driverPosition} icon={driverIcon} />
        )}
        <FitBounds points={fitPoints} />
      </MapContainer>
    </Box>
  );
};
